import { FormAutoComplete } from '../../common/form';
import { useMemo, useEffect } from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

const parseCode = (code: string) => {
  const match = code.match(/^(.*?)(?:_(\d+))?$/);
  return {
    prefix: (match && match[1].toUpperCase()) || '',
    sequence: match && match[2] ? parseInt(match[2], 10) : null,
  };
};

const getNextSequence = (sequences: any) =>
  sequences.length ? Math.max(...sequences) + 1 : 1;

export const AlgorithmCodeAutoComplete = (props: {
  algorithmCodes: string[];
  name: string;
}) => {
  const { algorithmCodes, name } = props;
  const { setValue } = useFormContext();
  const inputValue = useWatch({ name });
  const value = useWatch({ name: 'cloneValue' });

  const prefixMap = useMemo(() => {
    const prefixMap = new Map();

    algorithmCodes.forEach((code) => {
      const { prefix, sequence } = parseCode(code);
      if (!prefixMap.has(prefix)) {
        prefixMap.set(prefix, []);
      }
      if (sequence !== null) {
        prefixMap.get(prefix).push(sequence);
      }
    });
    return prefixMap;
  }, [algorithmCodes]);

  const suggestions = useMemo(() => {
    const inputPrefix = parseCode(inputValue || '').prefix.toUpperCase();
    const filteredSuggestions = [];

    prefixMap.forEach((sequences, prefix) => {
      if (prefix.toUpperCase().startsWith(inputPrefix)) {
        filteredSuggestions.push(`${prefix}_${getNextSequence(sequences)}`);
      }
    });

    if (inputPrefix && !prefixMap.has(inputPrefix)) {
      filteredSuggestions.push(`${inputPrefix}_1`);
    }

    return filteredSuggestions.map((s) => ({ value: s, label: s }));
  }, [inputValue, prefixMap]);

  const cloneValue = useMemo(() => {
    const { prefix } = parseCode(value || '');
    if (prefix && prefixMap.has(prefix)) {
      return `${prefix}_${getNextSequence(prefixMap.get(prefix))}`;
    }
  }, [prefixMap, value]);

  useEffect(() => {
    if (cloneValue) {
      setValue(name, cloneValue);
    }
  }, [cloneValue]);

  return (
    <StyledFormAutoComplete
      name={name}
      options={suggestions}
      key={cloneValue}
      filterOption={(input, option) =>
        String(option?.value).toUpperCase().includes(input.toUpperCase())
      }
    />
  );
};

const StyledFormAutoComplete = styled(FormAutoComplete)`
  min-width: 7rem;
`;
